/** @jsxImportSource @emotion/react */

import { FC, useEffect, useState } from "react";
import { css } from "@emotion/react";
import { useLocation, matchPath } from "react-router-dom";
import { DrawerAppContent } from "@rmwc/drawer";
import { SideBarProps } from "../Sidebar/Sidebar";
import Sidebar from "../Sidebar";
import TopAppBar, { AppBarProps } from "../TopAppBar";
import LinearProgress from "../../Spinner/LinearProgress";
import useConfigs from "../../ConfigContext/use-configs";
import Direction from "../../../commons/Direction";
import createSidebarMenus from "../../../commons/CreateSidebarMenus";
import useRoutes from "../../../commons/hooks/use-routes";
import usePermission from "../../../commons/hooks/use-permission";

type AppLayoutProps = {
  appBar?: AppBarProps;
  sideBar?: SideBarProps;
  loading?: boolean;
  dir?: Direction;
  useTitleRoute?: boolean;
};

const DefaultLayout: FC<AppLayoutProps> = (props) => {
  let configs = useConfigs();
  const { sideBar, appBar, children, loading, dir = configs.direction, useTitleRoute = false } = props;

  const { routes = [] } = useRoutes();
  const { permissions = [] } = usePermission();

  const [state, setState] = useState({
    sidebarItems: [],
    appBarTitle: appBar.title,
  });

  const location = useLocation();
  const currentPathname = location && location.pathname ? location.pathname : "";

  useEffect(() => {
    if (useTitleRoute) {
      let appBarTitle = state.appBarTitle;
      const matchPathName = (currentPathname: string = "", path: string = "", title: string) => {
        const match = matchPath(currentPathname, {
          path: path,
        });
        if (match && match.isExact && title) {
          appBarTitle = title;
        }
      };

      const loopRoute = (array: any = []) => {
        array.forEach((item: any) => {
          if (item.routes && item.routes.length) {
            loopRoute(item.routes);
          } else {
            matchPathName(currentPathname, item.path, item.title);
          }
        });
      };
      loopRoute(routes);
      setState((prevState) => ({ ...prevState, appBarTitle }));
    }
  }, [currentPathname, useTitleRoute, routes, state.appBarTitle]);

  useEffect(() => {
    const sidebarItems = createSidebarMenus(permissions, routes);
    setState((prevState) => ({
      ...prevState,
      sidebarItems,
    }));
  }, [permissions, routes]);

  const progressLoading = loading ? (
    <div
      css={css`
        .mdc-linear-progress {
          position: fixed;
          top: 0;
          z-index: 999;
        }
      `}
    >
      <LinearProgress reversed />
    </div>
  ) : null;

  const styles = css`
    .mdc-drawer--dismissible {
      position: fixed;
    }
  `;

  return (
    <div css={styles}>
      {progressLoading}
      <TopAppBar
        title={state.appBarTitle}
        backAction={appBar.backAction}
        backActionIgnorePaths={appBar.backActionIgnorePaths}
        actionItems={appBar.actionItems}
        dir={dir}
        menu={appBar.menu}
        topAppBarTitle={appBar.topAppBarTitle}
      >
        {(navOpen, closeNave) => (
          <Sidebar
            open={navOpen}
            onClose={closeNave}
            items={state.sidebarItems}
            currentRoutePath={currentPathname}
            headerIcon={sideBar.headerIcon}
            headerTitle={sideBar.headerTitle}
            headerSubtitle={sideBar.headerSubtitle}
            dir={dir}
          />
        )}
      </TopAppBar>
      <DrawerAppContent dir={dir}>{children}</DrawerAppContent>
    </div>
  );
};

export default DefaultLayout;
